<template>
  <el-card
    v-loading="loading"
    class="clear-card feedforward"
    shadow="hover"
    :body-style="{
      height: 'calc(100% - 95px)',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
    }"
  >
    <div
      slot="header"
      class="header defaultCursor"
    >
      <span>{{ $t('coach.leader.feedforward.title') }}</span>
    </div>
    <div class="content">
      <div
        v-if="filteredFeedforwards.length > 0"
        class="customScroll"
        :settings="scrollbarSettings"
      >
        <div class="list">
          <div
            v-for="(feedforward, key) in filteredFeedforwards"
            :key="key"
            class="feedforward"
          >
            <div class="supporter">
              {{ feedforward.supporter ? `${feedforward.supporter} -` : '' }}
              {{ feedforward.created_at | moment("YYYY-MM-DD") }}
            </div>
            <div class="suggestion">
              {{ feedforward.suggestion }}
            </div>
            <hr v-if="key !== filteredFeedforwards.length - 1 " />
          </div>
        </div>
      </div>

      <div
        v-if="filteredFeedforwards.length === 0"
        class="noData defaultCursor"
      >
        {{ $t('common.noData') }}
      </div>

      <div class="actionBtn">
        <el-row>
          <el-col :span="7">
            <el-button
              :type="(showLGA1 && !showAll) ? 'plain' : 'primary'"
              round
              @click="() => {
                showLGA1 = true; showLGA2 = false; showAll = false;
              }"
            >
              {{ $t('coach.leader.feedforward.lga') }} 1
            </el-button>
          </el-col>
          <el-col :span="7">
            <el-button
              :type="(showLGA2 && !showAll) ? 'plain' : 'primary'"
              round
              @click="() => {
                showLGA1 = false; showLGA2 = true; showAll = false;
              }"
            >
              {{ $t('coach.leader.feedforward.lga') }} 2
            </el-button>
          </el-col>
          <el-col :span="10">
            <el-button
              :type="showAll ? 'plain' : 'primary'"
              round
              class="viewAllBtn"
              @click="goToFeedforward"
            >
              {{ $t('coach.leader.feedforward.viewAll') }}
            </el-button>
          </el-col>
        </el-row>
      </div>
    </div>
  </el-card>
</template>

<script>
import api from '@/api';

export default {
  props: {
    leaderUuid: {
      type: String,
      default: '',
    },

    feedforwardsData: {
      type: Object,
      default: null,
    },

    loadFeedforwards: {
      type: Boolean,
      default: true,
    },

    fromPast: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      scrollbarSettings: {
        maxScrollbarLength: 300,
        minScrollbarLength: 50,
        scrollXMarginOffset: 10,
      },
      localFeedforwards: null,
      showLGA1: true,
      showLGA2: false,
      showAll: false,
      loading: false,
    };
  },

  computed: {
    feedforwards() {
      if (this.feedforwardsData) return this.feedforwardsData;
      return this.localFeedforwards;
    },

    filteredFeedforwards() {
      if (!this.feedforwards || this.feedforwards.length < 1) return [];

      let list = [];
      if ((this.showLGA1 || this.showAll) && this.feedforwards.lga1) {
        list = [...list, ...this.feedforwards.lga1];
      }
      if ((this.showLGA2 || this.showAll) && this.feedforwards.lga2) {
        list = [...list, ...this.feedforwards.lga2];
      }

      list = list.sort((a, b) => a.created_at - b.created_at);

      return list;
    },
  },

  watch: {
    showLGA1(newValue) {
      this.showAll = false;
      this.showLGA2 = !newValue;
    },

    showLGA2(newValue) {
      this.showAll = false;
      this.showLGA1 = !newValue;
    },

    leaderUuid() {
      // trigger session status update when navigate from leader to leader from leader alert
      this.getLeaderFeedforward();
    },
  },

  created() {
    if (this.loadFeedforwards) {
      this.getLeaderFeedforward();
    }
  },

  methods: {
    async getLeaderFeedforward() {
      try {
        this.loading = true;
        const response = await api.getCoachLeaderFeedForwards(this.leaderUuid);
        this.localFeedforwards = response.data;
        this.loading = false;
      } catch (err) {
        console.log(err);
        if (err.msg) {
          this.$message.error(err.msg);
        }
        this.loading = false;
      }
    },

    goToFeedforward() {
      const name = this.fromPast ? 'leaderEngagementFeedforward' : 'myLeaderFeedforward';
      this.$router.push({
        name, params: { id: this.$route.params.id },
      });
    },
  },

};
</script>

<style lang="scss" scoped>
.feedforward {
  .el-button {
    border-width: 3px;

    &.el-button--plain {
      border-color: white;
      border-width: 1px;
    }

    &.el-button--primary {
      background: transparent;
      border: 1px solid var(--themeFontColor);
      color: var(--themeFontColor);
    }
  }

  .header {
    text-align: center;
    font-weight: bold;
  }

  .content {
    text-align: left;

    .customScroll {
      position: relative;
      height: 300px;
      margin-bottom: 15px;
      scrollbar-color: #c8c8c8 transparent;
      overflow-y: scroll; /* has to be scroll, not auto */
      -webkit-overflow-scrolling: touch;

      &::-webkit-scrollbar {
        width: 5px;
      }

      &::-webkit-scrollbar-thumb {
        background: #c8c8c8;
        border-radius: 20px;
      }

      &::-webkit-scrollbar-track {
        background: transparent !important;
      }

      .list {
        padding-right: 10px;

        .supporter {
          font-weight: bold;
        }
      }
    }

    .noData {
      height: 300px;
      font-style: italic;
      text-align: center;
      display: flex;
      flex-direction: column;
      justify-content: space-around;
      color: #c4c4c4;
    }

    .actionBtn {
      text-align: center;

      .el-button {
        width: 90%;
        padding: 12px 10px;
      }

      .viewAllBtn {
        float: right;
      }
    }
  }
}
</style>
